import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="event-gallery-upload"
//
// Simple multi-file gallery uploader (no cropping — gallery photos are extras).
// - New files: previews rendered from the file input; removing one rebuilds the
//   input's FileList (a DataTransfer) so it isn't submitted.
// - Existing (persisted) photos: removing one hides its thumb and writes a hidden
//   remove_gallery_image_ids[] field so Eventable#update purges it on save.
export default class extends Controller {
  static targets = ["input", "thumbs", "removeFields"]
  static values = {
    max: Number,          // total gallery cap (existing + new)
    existing: Number,     // already-saved photos (before this session's removes)
    maxBytes: Number,     // per-file size cap
    tooMany: String,      // localized "up to N photos" message
    tooLarge: String,     // localized "each photo ≤ N MB" message
  }

  // A file input REPLACES its FileList each time the picker is opened — it never
  // accumulates. So on every change we append the newly-picked files to what's
  // already staged (via DataTransfer) and write the merged list back onto the
  // input. We also enforce the per-file size cap and the total count cap
  // (existing-not-removed + staged) for immediate feedback before save.
  addFiles() {
    const removed = this.removeFieldsTarget.querySelectorAll("input").length
    const existingKept = Math.max(0, (this.existingValue || 0) - removed)
    const remainingSlots = Math.max(0, (this.maxValue || Infinity) - existingKept)

    const dt = new DataTransfer()
    const seen = new Set()
    let rejectedSize = false
    ;[...(this._staged || []), ...Array.from(this.inputTarget.files)].forEach((file) => {
      const key = `${file.name}:${file.size}:${file.lastModified}`
      if (seen.has(key)) return
      if (this.maxBytesValue && file.size > this.maxBytesValue) { rejectedSize = true; return }
      seen.add(key)
      dt.items.add(file)
    })

    // Trim to the remaining slots (keep the first N).
    let overCount = false
    if (dt.files.length > remainingSlots) {
      const trimmed = new DataTransfer()
      Array.from(dt.files).slice(0, remainingSlots).forEach((f) => trimmed.items.add(f))
      this.inputTarget.files = trimmed.files
      overCount = true
    } else {
      this.inputTarget.files = dt.files
    }
    this._staged = Array.from(this.inputTarget.files)

    if (rejectedSize && this.tooLargeValue) window.displayFlashMessage?.("error", this.tooLargeValue)
    if (overCount && this.tooManyValue) window.displayFlashMessage?.("error", this.tooManyValue)

    this.previewNew()
  }

  previewNew() {
    // Clear any previously-rendered "new" previews, then render current selection.
    this.thumbsTarget.querySelectorAll(".gallery-thumb--new").forEach((el) => el.remove())
    Array.from(this.inputTarget.files).forEach((file, idx) => {
      const url = URL.createObjectURL(file)
      const thumb = document.createElement("div")
      thumb.className = "gallery-thumb gallery-thumb--new"
      thumb.dataset.newIdx = String(idx)
      thumb.innerHTML =
        `<img src="${url}" alt="">` +
        `<button type="button" class="gallery-thumb-remove" aria-label="Remove" data-action="event-gallery-upload#removeNew" data-new-idx="${idx}">&times;</button>`
      this.thumbsTarget.appendChild(thumb)
    })
  }

  removeNew(event) {
    const idx = Number(event.currentTarget.dataset.newIdx)
    const dt = new DataTransfer()
    Array.from(this.inputTarget.files).forEach((file, i) => {
      if (i !== idx) dt.items.add(file)
    })
    this.inputTarget.files = dt.files
    this._staged = Array.from(dt.files)
    this.previewNew()
  }

  removeExisting(event) {
    const attId = event.currentTarget.dataset.attId
    const thumb = event.currentTarget.closest(".gallery-thumb")
    if (thumb) thumb.remove()
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = "event[remove_gallery_image_ids][]"
    field.value = attId
    this.removeFieldsTarget.appendChild(field)
  }
}
